import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Slider, Typography, IconButton } from '@mui/material';
import { AccessTime, Schedule } from '@mui/icons-material';

interface TimeOfDayControlProps {
  renderer: any;
}

const formatTime = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = Math.floor(minutes % 60);
  const period = h >= 12 ? 'PM' : 'AM';
  const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayHour}:${m.toString().padStart(2, '0')} ${period}`;
};

const formatGmtOffset = (offset: number): string => {
  const sign = offset >= 0 ? '+' : '-';
  return `GMT${sign}${Math.abs(offset)}`;
};

const TimeOfDayControl: React.FC<TimeOfDayControlProps> = ({ renderer }) => {
  const [timeMinutes, setTimeMinutes] = useState<number>(720); // noon
  const [isCustomTime, setIsCustomTime] = useState<boolean>(false);
  const [gmtOffset, setGmtOffset] = useState<number>(0);
  const lastPoll = useRef(0);

  // Poll camera longitude to derive approximate timezone
  useEffect(() => {
    let rafId: number;
    const poll = () => {
      const now = performance.now();
      if (now - lastPoll.current > 2000 && renderer) {
        try {
          const loc = renderer.getCameraLocation();
          if (loc) {
            const offset = Math.round(loc.lon / 15);
            setGmtOffset(offset);

            // If not custom, update slider to current local time at camera
            if (!isCustomTime) {
              const utcNow = new Date();
              const localMs = utcNow.getTime() + offset * 3600000;
              const localDate = new Date(localMs);
              const mins = localDate.getUTCHours() * 60 + localDate.getUTCMinutes();
              setTimeMinutes(mins);
            }
          }
        } catch {}
        lastPoll.current = now;
      }
      rafId = requestAnimationFrame(poll);
    };
    rafId = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(rafId);
  }, [renderer, isCustomTime]);

  const handleTimeChange = useCallback((_event: Event, value: number | number[]) => {
    const minutes = value as number;
    setTimeMinutes(minutes);
    setIsCustomTime(true);

    // Slider is local time at camera — convert back to UTC for the renderer
    const utcMinutes = ((minutes - gmtOffset * 60) % 1440 + 1440) % 1440;
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1; // 1-indexed
    const day = now.getDate();
    const hour = Math.floor(utcMinutes / 60);
    const minute = Math.floor(utcMinutes % 60);

    if (renderer && renderer.setSkyDateTime) {
      renderer.setSkyDateTime(year, month, day, hour, minute, 0);
    }
  }, [renderer, gmtOffset]);

  const handleReset = useCallback(() => {
    setIsCustomTime(false);
    if (renderer && renderer.resetSkyToSystemClock) {
      renderer.resetSkyToSystemClock();
    }
  }, [renderer]);

  return (
    <Box>
      {/* Header row: label + timezone */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <AccessTime sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 18, mr: 1 }} />
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontWeight: 500,
            flex: 1,
            fontSize: '0.85rem',
          }}
        >
          Time of Day
        </Typography>
        {isCustomTime && (
          <IconButton
            onClick={handleReset}
            size="small"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              padding: '4px',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            }}
            title="Reset to system clock"
          >
            <Schedule sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      {/* Time + timezone display */}
      <Typography
        variant="caption"
        sx={{
          color: 'rgba(255, 255, 255, 0.6)',
          fontSize: '0.75rem',
          fontFamily: 'monospace',
          display: 'block',
          mb: 0.5,
        }}
      >
        {formatTime(timeMinutes)} · {formatGmtOffset(gmtOffset)}
      </Typography>

      {/* Slider */}
      <Slider
        value={timeMinutes}
        onChange={handleTimeChange}
        min={0}
        max={1440}
        step={1}
        sx={{
          color: 'white',
          '& .MuiSlider-thumb': {
            width: 14,
            height: 14,
            backgroundColor: 'white',
            '&:hover, &.Mui-focusVisible': {
              boxShadow: '0 0 0 6px rgba(255, 255, 255, 0.16)',
            },
          },
          '& .MuiSlider-track': {
            height: 3,
          },
          '& .MuiSlider-rail': {
            height: 3,
            opacity: 0.3,
          },
        }}
      />
    </Box>
  );
};

export default TimeOfDayControl;
